import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { UserTable } from "../components/users/UserTable";
import { AddUserDialog } from "../components/users/AddUserDialog";
import { EditUserDialog } from "../components/users/EditUserDialog";
import { DeleteUserDialog } from "../components/users/DeleteUserDialog";
import { ExportStudentsDialog } from "../components/users/ExportStudentsDialog";
import { userService, User } from "../services/userService";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/Button";
import { Download } from "lucide-react";
import { useServerPagination } from "@hooks/useServerPagination";
import { useDebounce } from "@hooks/useDebounce";

export default function StudentsPage() {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('analytics:export');

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Filter state (lifted from UserTable so the export dialog can read it)
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("student"); // students page is student-only

  // Server-side pagination
  const {
    data: users,
    isLoading,
    page,
    totalPages,
    total,
    setPage,
    refresh
  } = useServerPagination<User>({
    defaultLimit: 20,
    fetchData: useCallback(async ({ page, limit }) => {
      const params: {
        page: number;
        limit: number;
        search?: string;
        role?: string;
        isEmailVerified?: boolean;
      } = { page, limit };

      if (debouncedSearch) params.search = debouncedSearch;
      // Students page is student-only — keep the role pin so the export matches.
      params.role = roleFilter !== "all" ? roleFilter : "student";
      if (statusFilter === "verified") params.isEmailVerified = true;
      if (statusFilter === "unverified") params.isEmailVerified = false;

      const response = await userService.getAllUsers(params);
      return {
        data: response.users || [],
        total: response.total || 0,
        totalPages: response.totalPages || 1,
      };
    }, [debouncedSearch, statusFilter, roleFilter]),
    deps: [debouncedSearch, statusFilter, roleFilter],
  });

  // Dialog states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  const navigate = useNavigate();

  const handleViewClick = (user: User) => {
    navigate(`/students/${user._id}`);
  };

  // Wrapper for fetchUsers to use with dialogs
  const fetchUsers = async () => {
    refresh();
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6">
        <UserTable
          title="Students"
          description="Manage your students and their enrollment status."
          users={users}
          isLoading={isLoading}
          onAddClick={() => setIsAddOpen(true)}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onViewClick={handleViewClick}
          showRole={false}
          addButtonLabel="Add new student"
          initialRoleFilter="student"
          serverSideSearch={true}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={(s) => {
            setStatusFilter(s);
            setPage(1);
          }}
          roleFilter={roleFilter}
          onRoleFilterChange={(r) => {
            setRoleFilter(r);
            setPage(1);
          }}
          extraHeaderActions={
            canExport && (
              <Button
                variant="outline"
                size="lg"
                className="whitespace-nowrap gap-2 w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
                onClick={() => setIsExportOpen(true)}
              >
                <Download size={16} />
                Export CSV
              </Button>
            )
          }
          // Server-side pagination props
          serverSidePagination={true}
          totalItems={total}
          serverPage={page}
          serverTotalPages={totalPages}
          onPageChange={handlePageChange}
        />

        <AddUserDialog
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          onSuccess={fetchUsers}
          title="Add New Student"
          allowedRoles={["student"]}
          defaultRole="student"
        />

        <EditUserDialog
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedUser(null);
          }}
          onSuccess={fetchUsers}
          user={selectedUser}
          canChangeRole={false}
          allowedRoles={["student"]}
        />

        <DeleteUserDialog
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedUser(null);
          }}
          onSuccess={fetchUsers}
          user={selectedUser}
        />

        <ExportStudentsDialog
          isOpen={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          initialSearchTerm={searchTerm}
          initialVerificationStatus={
            statusFilter === "verified"
              ? "verified"
              : statusFilter === "unverified"
                ? "unverified"
                : "all"
          }
        />
      </div>
    </DashboardLayout>
  );
}